/**
 * DownloadButton Component
 */

import { pushPostAuthenticationRequestDownload } from '../../../../redux/actions/postAuthenticationRequests';
import { showSignUpForm } from '../../../../redux/actions/ui';
import { createHref } from '../../../../utils/router';
import './styles.scss';

import { template } from 'lodash';
import * as nextTick from 'next-tick';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';

import { DownloadContext } from '../../../../contexts/download';
import { SetFilePathHandler } from '../../../../contexts/download/types';
import { downloadTrack } from '../../../../redux/actions/download';
import { IconDownload } from '../../images/Icon';
import Button, { ButtonProps } from '../Button';

interface IOwnStateProps {
  isUserLoggedIn: boolean;
  location: IRouterLocation;
}

interface IOwnDispatchProps {
  signUpAndDownloadTrack(path: string, location: IRouterLocation): void;
  sendDownloadTrackEvent(): void;
}

interface IOwnMergedProps extends IOwnProps, IOwnStateProps, Pick<IOwnDispatchProps, 'sendDownloadTrackEvent'> {
  signUpAndDownloadTrack(path: string): void;
}

interface IDownloadContextProps {
  showAgreementModal: boolean;
  setDownloadFilePath: SetFilePathHandler;
}

interface IOwnProps extends ButtonProps {
  assetPath: string;
  stopPropagationOnClick?: boolean;
  track: GQL.ITrackSearchResult;
  onDownloadRequest?(): void;
}

interface IOwnState {
  downloadPath: string;
}

export type DownloadButtonOwnProps = IOwnProps;
type DownloadButtonProps = IOwnMergedProps & IDownloadContextProps & IOwnDispatchProps;

const classes = new BEM('download-button');
// tslint:disable-next-line:no-invalid-template-strings
export const downloadPathTemplate = template('/${path}?download');

class DownloadButton extends React.Component<DownloadButtonProps, IOwnState> {
  static defaultProps: Partial<DownloadButtonProps> = {
    label: 'Download Preview MP3',
  };

  static getDerivedStateFromProps(nextProps: DownloadButtonProps, prevState: IOwnState) {
    if (nextProps.assetPath !== prevState.downloadPath) {
      return {
        downloadPath: downloadPathTemplate({ path: nextProps.assetPath }),
      };
    }

    return null;
  }

  downloadPath: string;

  constructor(props) {
    super(props);

    this.state = {
      downloadPath: downloadPathTemplate({ path: this.props.assetPath }),
    };
  }

  onClickRequestDownload = () => {
    if (this.props.onDownloadRequest) {
      this.props.onDownloadRequest();
    }

    nextTick(() => {
      this.props.signUpAndDownloadTrack(this.state.downloadPath);
    });
  }

  onClickDownload = () => {
    this.props.setDownloadFilePath(this.state.downloadPath, this.props.track);
  }

  onClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    if (this.props.stopPropagationOnClick) {
      e.stopPropagation();
    }

    if (!this.props.isUserLoggedIn) {
      this.onClickRequestDownload();
    } else if (this.props.showAgreementModal) {
      this.onClickDownload();
    } else {
      this.props.sendDownloadTrackEvent();
    }

    if (this.props.onClick) {
      e.persist();
      this.props.onClick(e);
    }
  }

  render() {
    const {
      className,
      assetPath,
      isUserLoggedIn,
      label,
      location,
      onClick,
      onDownloadRequest,
      sendDownloadTrackEvent,
      setDownloadFilePath,
      showAgreementModal,
      stopPropagationOnClick,
      signUpAndDownloadTrack,
      track,
      ...buttonProps
    } = this.props;

    const buttonLabel = !buttonProps.variant || buttonProps.variant !== 'icon' ? label : undefined;

    return showAgreementModal || !isUserLoggedIn ? (
      <Button
        onClick={this.onClick}
        label={buttonLabel}
        icon={<IconDownload />}
        {...classes('', '', className)}
        {...buttonProps}
      />
    ) : (
        <Button
          onClick={this.onClick}
          hyperLinkProps={{
            href: this.state.downloadPath,
          }}
          wrapperComponent="a"
          label={buttonLabel}
          icon={<IconDownload />}
          {...classes('', '', className)}
          {...buttonProps}
        />
      );
  }
}

const ConnectedDownloadButton =
  connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & IDownloadContextProps, IOwnMergedProps, IStoreState>(
    (state) => ({
      location: state.found.match.location,
      isUserLoggedIn: state.user.isLoggedIn,
    }),
    (dispatch, props) => ({
      sendDownloadTrackEvent: () => dispatch(downloadTrack({ track: props.track })),
      signUpAndDownloadTrack: (path, location) => {
        dispatch(pushPostAuthenticationRequestDownload({
          path,
          page: createHref(location),
          // Full track search result is too big to pass as post-authentication
          // state, so we only take the essensials
          track: {
            title: props.track.title,
            artist: props.track.artist ? { name: props.track.artist!.name } : undefined,
          } as any,
        }));
        nextTick(() => dispatch(showSignUpForm({ reason: 'download' })));
      },
    }),
    (stateProps, { sendDownloadTrackEvent, ...dispatchProps }, ownProps) => ({
      ...stateProps,
      ...ownProps,
      sendDownloadTrackEvent,
      signUpAndDownloadTrack: (path) => {
        dispatchProps.signUpAndDownloadTrack(path, stateProps.location);
      },
    }),
  )(DownloadButton);

const DownloadButtonWithDownloadContext: React.SFC<IOwnProps> = (props) => (
  <DownloadContext.Consumer>
    {({ agreementVisibility, setFilePath }) => {
      return (
        <ConnectedDownloadButton
          setDownloadFilePath={setFilePath}
          showAgreementModal={agreementVisibility}
          {...props}
        />
      );
    }}
  </DownloadContext.Consumer>
);

export const SignUpGuardedDownloadButton = DownloadButtonWithDownloadContext;

export default DownloadButtonWithDownloadContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/buttons/DownloadButton/index.tsx